/**
 * Life4Earth V2 — Workspace AI Interaction API Adapter
 *
 * CRUD for AI sessions, evidence items, clarifying questions,
 * prompt template resolution, and enriched phase transitions.
 *
 * Endpoint: /api/ProblemWorkspaces/{workspaceGuid}/ai
 */

import { apiFetch } from "./client";

// ═══════════════════════════════════════════════════════════════════════════
// Response Types
// ═══════════════════════════════════════════════════════════════════════════

export interface AiSessionResponse {
  id: string;
  sessionGuid: string;
  workspaceGuid: string;
  phase: string;
  sessionType: string;
  templateGuid: string | null;
  modelId: string | null;
  inputPayload: string;
  outputPayload: string | null;
  rawOutput: string | null;
  confidenceScore: number | null;
  confidenceRationale: string | null;
  status: AiSessionStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  /** Enriched: count of clarifying questions generated by this session */
  clarifyingQuestionCount: number;
  /** Enriched: count of evidence items recommended by this session */
  evidenceItemCount: number;
  /** Assembled prompt text (populated on create for simulation mode copy-paste) */
  assembledPrompt?: string | null;
}

export type AiSessionStatus = "pending" | "completed" | "reviewed" | "superseded";

export interface ClarifyingQuestionResponse {
  id: string;
  questionGuid: string;
  sessionGuid: string;
  workspaceGuid: string;
  sortOrder: number;
  questionText: string;
  answerText: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
  createdAt: string;
}

export type SourceStatus = "unknown" | "active" | "broken" | "archived";

export interface EvidenceItemResponse {
  id: string;
  evidenceGuid: string;
  workspaceGuid: string;
  recommendedBySession: string | null;
  evidenceType: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceName: string | null;
  sourceStatus: SourceStatus;
  fileKey: string | null;
  collectionGuidance: string | null;
  collectionNotes: string | null;
  status: EvidenceItemStatus;
  priority: number;
  assignedTo: string | null;
  collectedAt: string | null;
  analyzedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type EvidenceItemStatus =
  | "recommended"
  | "accepted"
  | "collecting"
  | "collected"
  | "analyzed"
  | "rejected";

export interface PromptTemplateResponse {
  id: string;
  templateGuid: string;
  phase: string;
  category: string | null;
  templateName: string;
  description: string | null;
  sessionType: string;
  systemPrompt: string;
  userPromptTemplate: string;
  responseSchema: string;
  version: number;
  isActive: boolean;
  createdAt: string;
}

export interface AssembledPromptResponse {
  templateGuid: string;
  templateName: string;
  phase: string;
  sessionType: string;
  systemPrompt: string;
  userPrompt: string;
  /** Combined "SYSTEM:\n...\n\nUSER:\n..." for easy copy-paste in simulation mode */
  combinedPrompt: string;
}

export interface PhaseLogResponse {
  id: string;
  workspaceGuid: string;
  fromPhase: string;
  toPhase: string;
  direction: "forward" | "backward";
  rationale: string | null;
  confidenceAtTransition: number | null;
  evidenceCountAtTransition: number;
  transitionedBy: string;
  transitionedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Request Types
// ═══════════════════════════════════════════════════════════════════════════

export interface CreateAiSessionRequest {
  sessionType: string;
  templateGuid?: string;
  modelId?: string;
}

export interface CompleteAiSessionRequest {
  rawOutput?: string;
  outputPayload?: string;
  confidenceScore?: number;
  confidenceRationale?: string;
}

export interface ReviewAiSessionRequest {
  notes?: string;
}

export interface CreateClarifyingQuestionRequest {
  questionText: string;
  sortOrder: number;
}

export interface AnswerClarifyingQuestionRequest {
  answerText: string;
}

export interface CreateEvidenceItemRequest {
  evidenceType: string;
  title: string;
  description?: string;
  sourceUrl?: string;
  sourceName?: string;
  fileKey?: string;
  collectionGuidance?: string;
  recommendedBySession?: string;
  priority?: number;
  assignedTo?: string;
}

export interface UpdateEvidenceItemRequest {
  title?: string;
  description?: string;
  sourceUrl?: string;
  sourceName?: string;
  sourceStatus?: SourceStatus;
  fileKey?: string;
  collectionGuidance?: string;
  collectionNotes?: string;
  status?: EvidenceItemStatus;
  priority?: number;
  assignedTo?: string;
}

export interface TransitionPhaseWithContextRequest {
  toPhase: string;
  rationale?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Evidence Type Constants
// ═══════════════════════════════════════════════════════════════════════════

export const EVIDENCE_TYPES = [
  "photograph",
  "interview",
  "document",
  "report",
  "survey",
  "inspection",
  "dataset",
  "government_report",
  "un_report",
] as const;

export type EvidenceType = typeof EVIDENCE_TYPES[number];

// ═══════════════════════════════════════════════════════════════════════════
// URL helper
// ═══════════════════════════════════════════════════════════════════════════

const aiBase = (workspaceGuid: string) =>
  `/api/ProblemWorkspaces/${workspaceGuid}/ai`;

// ═══════════════════════════════════════════════════════════════════════════
// AI Sessions
// ═══════════════════════════════════════════════════════════════════════════

/** List AI sessions for a workspace, optionally filtered by phase and/or session type. */
export async function fetchAiSessions(
  workspaceGuid: string,
  params?: { phase?: string; sessionType?: string }
): Promise<AiSessionResponse[]> {
  const qs = new URLSearchParams();
  if (params?.phase) qs.set("phase", params.phase);
  if (params?.sessionType) qs.set("sessionType", params.sessionType);
  const query = qs.toString();
  return apiFetch<AiSessionResponse[]>(
    `${aiBase(workspaceGuid)}/sessions${query ? `?${query}` : ""}`
  );
}

/** Get a single AI session by its session GUID. */
export async function fetchAiSession(
  workspaceGuid: string,
  sessionGuid: string
): Promise<AiSessionResponse> {
  return apiFetch<AiSessionResponse>(
    `${aiBase(workspaceGuid)}/sessions/${sessionGuid}`
  );
}

/**
 * Start a new AI session.
 * Auto-resolves the prompt template by phase + session type + category.
 * Returns the session with an assembled prompt for simulation mode copy-paste.
 */
export async function createAiSession(
  workspaceGuid: string,
  request: CreateAiSessionRequest
): Promise<AiSessionResponse> {
  return apiFetch<AiSessionResponse>(
    `${aiBase(workspaceGuid)}/sessions`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/**
 * Complete a pending session by submitting the AI response.
 * In simulation mode, the user pastes the AI response as rawOutput.
 */
export async function completeAiSession(
  workspaceGuid: string,
  sessionGuid: string,
  request: CompleteAiSessionRequest
): Promise<AiSessionResponse> {
  return apiFetch<AiSessionResponse>(
    `${aiBase(workspaceGuid)}/sessions/${sessionGuid}/complete`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

/**
 * Re-parse an evidence_plan session's AI response to create evidence items.
 * Useful for sessions that were completed before the auto-parse logic existed.
 */
export async function reprocessEvidencePlan(
  workspaceGuid: string,
  sessionGuid: string
): Promise<{ created: number; sessionGuid: string }> {
  return apiFetch<{ created: number; sessionGuid: string }>(
    `${aiBase(workspaceGuid)}/sessions/${sessionGuid}/reprocess-evidence`,
    { method: "POST" }
  );
}

/** Mark a completed session as human-reviewed. */
export async function reviewAiSession(
  workspaceGuid: string,
  sessionGuid: string,
  request: ReviewAiSessionRequest = {}
): Promise<AiSessionResponse> {
  return apiFetch<AiSessionResponse>(
    `${aiBase(workspaceGuid)}/sessions/${sessionGuid}/review`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Prompt Assembly (simulation mode)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Assemble a prompt from the resolved template + workspace context.
 * Returns the combined prompt text for copy-paste into an external AI.
 */
export async function assemblePrompt(
  workspaceGuid: string,
  sessionType: string
): Promise<AssembledPromptResponse> {
  return apiFetch<AssembledPromptResponse>(
    `${aiBase(workspaceGuid)}/prompts/assemble?sessionType=${encodeURIComponent(sessionType)}`
  );
}

/** List available prompt templates for this workspace's current phase. */
export async function fetchPromptTemplates(
  workspaceGuid: string,
  phase?: string
): Promise<PromptTemplateResponse[]> {
  const qs = phase ? `?phase=${encodeURIComponent(phase)}` : "";
  return apiFetch<PromptTemplateResponse[]>(
    `${aiBase(workspaceGuid)}/prompts/templates${qs}`
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Clarifying Questions
// ═══════════════════════════════════════════════════════════════════════════

/** Get all clarifying questions for a specific AI session. */
export async function fetchClarifyingQuestions(
  workspaceGuid: string,
  sessionGuid: string
): Promise<ClarifyingQuestionResponse[]> {
  return apiFetch<ClarifyingQuestionResponse[]>(
    `${aiBase(workspaceGuid)}/sessions/${sessionGuid}/questions`
  );
}

/** Add a clarifying question to a session (from AI response parsing). */
export async function createClarifyingQuestion(
  workspaceGuid: string,
  sessionGuid: string,
  request: CreateClarifyingQuestionRequest
): Promise<ClarifyingQuestionResponse> {
  return apiFetch<ClarifyingQuestionResponse>(
    `${aiBase(workspaceGuid)}/sessions/${sessionGuid}/questions`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/** Submit a team answer for a clarifying question. */
export async function answerClarifyingQuestion(
  workspaceGuid: string,
  questionGuid: string,
  request: AnswerClarifyingQuestionRequest
): Promise<ClarifyingQuestionResponse> {
  return apiFetch<ClarifyingQuestionResponse>(
    `${aiBase(workspaceGuid)}/questions/${questionGuid}/answer`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

/** Get all unanswered clarifying questions across all sessions for a workspace. */
export async function fetchUnansweredQuestions(
  workspaceGuid: string
): Promise<ClarifyingQuestionResponse[]> {
  return apiFetch<ClarifyingQuestionResponse[]>(
    `${aiBase(workspaceGuid)}/questions/unanswered`
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Evidence Items
// ═══════════════════════════════════════════════════════════════════════════

/** List evidence items for a workspace, optionally filtered by status. */
export async function fetchEvidenceItems(
  workspaceGuid: string,
  status?: EvidenceItemStatus
): Promise<EvidenceItemResponse[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch<EvidenceItemResponse[]>(
    `${aiBase(workspaceGuid)}/evidence${qs}`
  );
}

/** Get a single evidence item by its evidence GUID. */
export async function fetchEvidenceItem(
  workspaceGuid: string,
  evidenceGuid: string
): Promise<EvidenceItemResponse> {
  return apiFetch<EvidenceItemResponse>(
    `${aiBase(workspaceGuid)}/evidence/${evidenceGuid}`
  );
}

/** Create a new evidence item (manual or AI-recommended). */
export async function createEvidenceItem(
  workspaceGuid: string,
  request: CreateEvidenceItemRequest
): Promise<EvidenceItemResponse> {
  return apiFetch<EvidenceItemResponse>(
    `${aiBase(workspaceGuid)}/evidence`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/** Update an existing evidence item (supports partial updates). */
export async function updateEvidenceItem(
  workspaceGuid: string,
  evidenceGuid: string,
  request: UpdateEvidenceItemRequest
): Promise<EvidenceItemResponse> {
  return apiFetch<EvidenceItemResponse>(
    `${aiBase(workspaceGuid)}/evidence/${evidenceGuid}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

/** Delete an evidence item. */
export async function deleteEvidenceItem(
  workspaceGuid: string,
  evidenceGuid: string
): Promise<void> {
  return apiFetch<void>(
    `${aiBase(workspaceGuid)}/evidence/${evidenceGuid}`,
    { method: "DELETE" }
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase Transition (enriched — with audit log)
// ═══════════════════════════════════════════════════════════════════════════

/** Get the full phase transition audit log for a workspace. */
export async function fetchPhaseLog(
  workspaceGuid: string
): Promise<PhaseLogResponse[]> {
  return apiFetch<PhaseLogResponse[]>(
    `${aiBase(workspaceGuid)}/phase-log`
  );
}

/**
 * Enriched phase transition: logs to audit trail, detects direction,
 * supersedes old AI sessions on backward transitions.
 */
export async function transitionPhaseWithContext(
  workspaceGuid: string,
  request: TransitionPhaseWithContextRequest
): Promise<PhaseLogResponse> {
  return apiFetch<PhaseLogResponse>(
    `${aiBase(workspaceGuid)}/transition`,
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}
